import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Trans, t } from '@lingui/macro'
import { Formik, type FormikHelpers } from 'formik'
import { useContext, useEffect, useState } from 'react'
import styled from 'styled-components'
import * as Yup from 'yup'

import { SearchCombobox } from '@/@bothrs-ui/web/components/forms/search-combobox'
import { InfoPanel } from '@/@bothrs-ui/web/components/info-panel/InfoPanel'
import { Button } from '@/@bothrs-ui/web/components/primitives/button/Button'
import { Span } from '@/@bothrs-ui/web/components/typography/Body'
import { mixpanel } from '@/mixpanel'
import { isMedicationFileProcessed } from '@/modules/app/utils/medication.utils'
import {
  type DetailInputMode,
  StructuredObservation,
  useStructuredObservationDialog,
} from '@/modules/questionnaire/components/question-observation/structured-observation/StructuredObservation'
import {
  type CapturedMedicationPhoto,
  useMedicationPhotoUpload,
} from '@/modules/questionnaire/hooks/useMedicationPhotoUpload'
import { useMedicationSearch } from '@/modules/questionnaire/hooks/useMedicationSearch'
import { useQuestionnaireTrackingProps } from '@/modules/questionnaire/hooks/useQuestionnaireTrackingProps'
import { ImagePreviewModal } from '@/modules/shared/components/image-preview-modal/ImagePreviewModal'
import { useToast } from '@/modules/shared/hooks/useToast'
import { useFeatureFlag } from '@/utils/feature-flag'
import { getDefaultDosageRows, getUnitOptions } from '../MedicationDosage.constants'
import { MedicationFileProcessed } from '../MedicationFileProcessed'
import { MedicationPhotoButton } from '../MedicationPhotoButton'
import { MedicationPhotoThumbnail } from '../MedicationPhotoThumbnail'
import { MedicationContext } from '../MedicationQuestion.context'
import {
  formValuesToMedicationStatement,
  isMedicationSaveDisabled,
  type MedicationFormValues,
  medicationStatementToFormValues,
  resolveDosageMode,
} from '../MedicationQuestion.helpers'
import { DOSAGE_INPUT_MIN_HEIGHT, MedicationDosageStructuredInput } from './MedicationDosageStructuredInput'
import { MedicationPhotoCard } from './MedicationPhotoCard'
import { MedicationPhotoConfirm } from './MedicationPhotoConfirm'
import { MedicationPhotoError } from './MedicationPhotoError'
import { useMedicationFileParse } from './useMedicationFileParse'

import type {
  MedicationPhotoFallbackReason,
  MedicationSearchResult,
  MedicationStatement,
} from '@/modules/app/types/HealthParameter.types'
import type { SelectedMedication } from '../MedicationQuestion.context'

// ================================ Validation ================================

const validationSchema = Yup.object().shape({
  name: Yup.string()
    .required(
      t({
        id: 'medication.form.name.required',
        message: 'Medicatienaam is verplicht',
      }),
    )
    .max(
      100,
      t({
        id: 'medication.form.name.maxLength',
        message: 'Naam mag maximaal 100 tekens bevatten',
      }),
    ),
  dosageInstructions: Yup.string().max(
    500,
    t({
      id: 'medication.form.dosageInstructions.maxLength',
      message: 'Beschrijf hoeveel en wanneer je je medicatie inneemt (max 500 tekens)',
    }),
  ),
})

// ================================ Component ================================

export const MedicationItemForm = () => {
  const {
    medications,
    isPhotoCaptureEnabled,
    addMedication,
    editMedication,
    addSchemeFile,
    storedFiles,
    isPhotoInfoOpen,
    setPhotoInfoOpen,
  } = useContext(MedicationContext)!
  const { dialog, closeDialog } = useStructuredObservationDialog()

  const medicationToEdit =
    dialog.mode === 'update' ? (medications.find((m) => m._clientId === dialog.itemId) ?? null) : null

  const initialSelected: SelectedMedication | null = medicationToEdit ? { name: medicationToEdit.name } : null

  const [selectedMedication, setSelectedMedication] = useState<SelectedMedication | null>(initialSelected)

  const [searchQuery, setSearchQuery] = useState('')
  const { results } = useMedicationSearch(searchQuery)
  const trackingProps = useQuestionnaireTrackingProps()

  const initialDosageMode: DetailInputMode = medicationToEdit ? resolveDosageMode(medicationToEdit) : 'structured'
  const [dosageMode, setDosageMode] = useState<DetailInputMode>(initialDosageMode)

  const photoParsingEnabled = Boolean(useFeatureFlag('medication_photo_parsing'))
  const { takePhoto, isParsing, error: photoError } = useMedicationFileParse()
  const { uploadPhoto, isUploading } = useMedicationPhotoUpload()
  const [attachment, setAttachment] = useState<CapturedMedicationPhoto | null>(null)

  // A photo just taken shows from memory; on edit, the line's stored photo
  // comes from the question's urls. The edit keeps that stored photo, so it
  // stays in view until saved, as its stand-in once the care team processed it.
  const storedFileId = medicationToEdit?.attachment?.fileId
  const storedFile = storedFileId ? storedFiles.get(storedFileId) : undefined
  const photoUrl = attachment?.url ?? storedFile?.url
  const isPhotoProcessed = !attachment && isMedicationFileProcessed(storedFile)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)

  // The panel belongs to one trip through the dialog: the form mounts per
  // opening, so a reopen starts on the form rather than on the panel.
  useEffect(() => {
    setPhotoInfoOpen(false)
  }, [setPhotoInfoOpen])
  const { showErrorToast } = useToast()

  /**
   * Stores a photo once the patient confirmed it, and reports a failure. The
   * photo is only uploaded here, so one the patient discards never is.
   */
  const storePhoto = async (photo: CapturedMedicationPhoto) => {
    const stored = await uploadPhoto(photo)

    if (!stored) {
      showErrorToast({
        message: t({
          id: 'questionnaire.file-upload.error',
          comment: 'Error message for file upload',
          message: 'Er is een fout opgetreden bij het uploaden van de foto',
        }),
      })
    }

    return stored
  }

  /**
   * The photo waiting to be handed over, set when the parser could not read a
   * single medication from it. Holding it here keeps the dialog on the confirm
   * step until the patient decides.
   */
  const [photoToConfirm, setPhotoToConfirm] = useState<{
    photo: CapturedMedicationPhoto
    reason: MedicationPhotoFallbackReason | null
  } | null>(null)

  /**
   * Runs the capture, then routes to one of three outcomes: prefill the form,
   * ask whether to hand the photo over, or report that we could not process it.
   *
   * With parsing switched off there is nothing to route on, so the photo goes
   * straight to the care team. Asking the patient to confirm a photo they just
   * chose to take, about a reading that never happened, would be noise.
   */
  const handlePhoto = async (setFieldValue: (field: string, value: unknown) => void) => {
    const result = await takePhoto({ parse: photoParsingEnabled })

    if (!result) {
      // Null is either a cancelled camera, which needs no message, or a failure
      // the hook recorded and the error view reads.
      return
    }

    const photo: CapturedMedicationPhoto = { url: result.fileUrl, takenAt: new Date().toISOString() }

    if (!photoParsingEnabled) {
      const stored = await storePhoto(photo)

      if (!stored) {
        // Nothing holds the photo now, so release it; the patient can take it again.
        URL.revokeObjectURL(photo.url)

        return
      }

      addSchemeFile(stored)
      closeDialog()

      return
    }

    if (result.medications.length !== 1) {
      setPhotoToConfirm({ photo, reason: result.fallbackReason })

      return
    }

    // Name and unit are prefilled. The api still reads intake and amount from
    // the packaging, but the patient enters those themselves for now.
    const { name, unit } = result.medications[0]

    // The api and this app keep their own copies of the unit list, so a value
    // the options do not know is possible once they drift apart.
    const readUnit = unit ? getUnitOptions().find((option) => option.value === unit)?.value : undefined

    setSelectedMedication({ name })
    setAttachment(photo)
    setFieldValue('name', name)
    setFieldValue('code', null)

    // Left at the default when the form could not be read, rather than guessing
    // at pills: a syrup opened as pills is a wrong number of something.
    if (readUnit) {
      setFieldValue(
        'dosageRows',
        getDefaultDosageRows().map((row) => ({ ...row, unit: readUnit })),
      )
    }
  }

  const confirmPhoto = async () => {
    if (!photoToConfirm) {
      return
    }

    const stored = await storePhoto(photoToConfirm.photo)

    if (!stored) {
      return
    }

    addSchemeFile(stored)
    setPhotoToConfirm(null)
    closeDialog()
  }

  const retakePhoto = () => {
    URL.revokeObjectURL(photoToConfirm?.photo.url ?? '')
    setPhotoToConfirm(null)
  }

  // Only offered while adding: on edit the medication is already chosen, and a
  // photo would silently replace what the patient came here to correct.
  const showPhotoCard = isPhotoCaptureEnabled && dialog.mode !== 'update' && !selectedMedication && !searchQuery

  const initialValues: MedicationFormValues = medicationToEdit
    ? medicationStatementToFormValues(medicationToEdit)
    : {
        name: '',
        dosageInstructions: '',
        dosageRows: getDefaultDosageRows(),
        asNeededBoolean: false,
      }

  const saveMedication = (medication: MedicationStatement) => {
    if (dialog.mode === 'update') {
      editMedication(dialog.itemId, medication)
    } else {
      addMedication(medication)
    }
    closeDialog()
  }

  const handleSubmit = async (values: MedicationFormValues, { resetForm }: FormikHelpers<MedicationFormValues>) => {
    const medication = formValuesToMedicationStatement(values, dosageMode)
    const stored = attachment && (await storePhoto(attachment))

    if (attachment && !stored) {
      return
    }

    saveMedication(stored ? { ...medication, attachment: stored } : medication)
    resetForm()
    setSelectedMedication(null)
    setAttachment(null)
  }

  return (
    <Formik
      key={dialog.mode === 'update' ? dialog.itemId : 'new'}
      initialValues={initialValues}
      validationSchema={validationSchema}
      enableReinitialize
      onSubmit={handleSubmit}
    >
      {({ isValid, isSubmitting, setFieldValue, values }) => {
        if (photoError) {
          return (
            <MedicationPhotoError>
              <MedicationPhotoError.Illustration src='/img/svg/timeline-error.svg' alt='' />

              <MedicationPhotoError.Title>
                {photoError === 'rate_limited'
                  ? t({
                      id: 'medication.form.photo.error.rateLimited.title',
                      message: 'Even geduld',
                    })
                  : t({
                      id: 'medication.form.photo.error.title',
                      message: 'We konden je foto niet verwerken',
                    })}
              </MedicationPhotoError.Title>

              <MedicationPhotoError.Description>
                {photoError === 'rate_limited'
                  ? t({
                      id: 'medication.form.photo.error.rateLimited.description',
                      message:
                        "Je stuurde net veel foto's na elkaar. Er is nog niets aan je lijst toegevoegd. Probeer het over een minuutje opnieuw, of vul je medicatie zelf in.",
                    })
                  : t({
                      id: 'medication.form.photo.error.description',
                      message:
                        'Er ging iets mis aan onze kant. Er is nog niets aan je lijst toegevoegd. Lukt het opnieuw niet, dan kan je je medicatie ook zelf invullen.',
                    })}
              </MedicationPhotoError.Description>

              <MedicationPhotoError.Actions>
                <Button
                  type='button'
                  variant='Secondary'
                  isLoading={isParsing}
                  onClick={() => handlePhoto(setFieldValue)}
                >
                  {t({
                    id: 'medication.form.photo.error.retry',
                    message: 'Opnieuw proberen',
                  })}
                </Button>
              </MedicationPhotoError.Actions>
            </MedicationPhotoError>
          )
        }

        if (photoToConfirm) {
          return (
            <MedicationPhotoConfirm>
              <MedicationPhotoConfirm.Question>
                {photoToConfirm.reason === 'scheme'
                  ? t({
                      id: 'medication.form.photo.confirm.scheme',
                      message:
                        'Dit lijkt een medicatieschema. Wil je deze foto toevoegen zodat we deze kunnen verwerken?',
                    })
                  : photoToConfirm.reason === 'multiple'
                    ? t({
                        id: 'medication.form.photo.confirm.multiple',
                        message:
                          'We lazen meerdere geneesmiddelen uit deze foto. Wil je deze foto toevoegen zodat we deze kunnen verwerken?',
                      })
                    : t({
                        id: 'medication.form.photo.confirm.unreadable',
                        message:
                          'We konden geen geneesmiddel uit deze foto lezen. Wil je deze foto toevoegen zodat we deze kunnen verwerken?',
                      })}
              </MedicationPhotoConfirm.Question>

              <MedicationPhotoConfirm.Preview
                src={photoToConfirm.photo.url}
                alt={t({
                  id: 'medication.list.photoAlt',
                  message: 'Foto van je medicatie',
                })}
              />

              <MedicationPhotoConfirm.Actions>
                <Button type='button' variant='Primary' isLoading={isUploading} onClick={confirmPhoto}>
                  {t({
                    id: 'medication.form.photo.confirm.add',
                    message: 'Foto toevoegen',
                  })}
                </Button>
                <Button type='button' variant='Secondary' isLoading={isParsing} onClick={retakePhoto}>
                  {t({
                    id: 'medication.form.photo.confirm.retake',
                    message: 'Nieuwe foto nemen',
                  })}
                </Button>
              </MedicationPhotoConfirm.Actions>
            </MedicationPhotoConfirm>
          )
        }

        if (isPhotoInfoOpen) {
          return (
            <InfoPanel>
              <section>
                <InfoPanel.Heading>
                  {t({
                    id: 'medication.form.photo.info.usage.heading',
                    message: 'Medicatie herkennen en aanvullen',
                  })}
                </InfoPanel.Heading>
                <InfoPanel.Body>
                  {t({
                    id: 'medication.form.photo.info.usage.body',
                    message:
                      'AI leest de naam van je medicatie op de verpakking en vult die alvast voor je in. Jij controleert of alles klopt. Vervolgens vul je zelf aan hoe vaak je de medicatie inneemt.',
                  })}
                </InfoPanel.Body>
              </section>

              <section>
                <InfoPanel.Heading>
                  {t({
                    id: 'medication.form.photo.info.access.heading',
                    message: 'Extra controle door zorgverlener',
                  })}
                </InfoPanel.Heading>
                <InfoPanel.Body>
                  {t({
                    id: 'medication.form.photo.info.access.body',
                    message:
                      'Een zorgverlener controleert de gegevens. Pas nadien wordt de informatie toegevoegd aan je patiëntendossier.',
                  })}
                </InfoPanel.Body>
              </section>

              <section>
                <InfoPanel.Heading>
                  {t({
                    id: 'medication.form.photo.info.retention.heading',
                    message: 'Tijdelijk bewaard',
                  })}
                </InfoPanel.Heading>
                <InfoPanel.Body>
                  {t({
                    id: 'medication.form.photo.info.retention.body',
                    message:
                      'Je foto wordt tijdelijk bewaard in een beveiligde omgeving van Maria Middelares. Zodra je gegevens verwerkt zijn, wordt de foto verwijderd.',
                  })}
                </InfoPanel.Body>
              </section>

              <section>
                <InfoPanel.Heading>
                  {t({
                    id: 'medication.form.photo.info.location.heading',
                    message: 'Verwerking binnen Europa',
                  })}
                </InfoPanel.Heading>
                <InfoPanel.Body>
                  {t({
                    id: 'medication.form.photo.info.location.body',
                    message:
                      'Daarnaast wordt je foto verwerkt door een AI-dienst van Microsoft. Deze verwerking gebeurt binnen Europa. Je gegevens worden niet gebruikt om het AI-model te trainen.',
                  })}
                </InfoPanel.Body>
              </section>

              <section>
                <InfoPanel.Heading>
                  {t({
                    id: 'medication.form.photo.info.unreadable.heading',
                    message: 'Herkent AI je medicatie niet?',
                  })}
                </InfoPanel.Heading>
                <InfoPanel.Body>
                  {t({
                    id: 'medication.form.photo.info.unreadable.body',
                    message:
                      'Dan kan je ervoor kiezen de foto door te sturen naar een zorgverlener. Die bekijkt de foto en vult de informatie verder aan.',
                  })}
                </InfoPanel.Body>
              </section>

              <section>
                <InfoPanel.Heading>
                  {t({
                    id: 'medication.form.photo.info.optional.heading',
                    message: 'Niet verplicht',
                  })}
                </InfoPanel.Heading>
                <InfoPanel.Body>
                  {t({
                    id: 'medication.form.photo.info.optional.body',
                    message:
                      'Een foto nemen is niet verplicht. Je kan de informatie over je medicatie ook handmatig toevoegen via de zoekbalk.',
                  })}
                </InfoPanel.Body>
              </section>

              <section>
                <InfoPanel.Heading>
                  {t({
                    id: 'medication.form.photo.info.contact.heading',
                    message: 'Vragen?',
                  })}
                </InfoPanel.Heading>
                <InfoPanel.Body>
                  {t({
                    id: 'medication.form.photo.info.contact.body',
                    message: 'Neem contact op via 09 246 75 75 bij vragen over je gegevens(verwerking).',
                  })}
                </InfoPanel.Body>
              </section>
            </InfoPanel>
          )
        }

        return (
          <StructuredObservation.ItemFormLayout
            showDetailInput={!!selectedMedication}
            detailMode={dosageMode}
            onDetailModeChange={setDosageMode}
            structuredInput={<MedicationDosageStructuredInput />}
            freeInput={
              <StructuredObservation.FreeInput
                fieldName='dosageInstructions'
                label={t({
                  id: 'medication.form.frequency.freeTextLabel',
                  message: 'Beschrijf hoeveel en wanneer je je medicatie inneemt',
                })}
                placeholder={t({
                  id: 'medication.form.dosageInstructions.placeholder',
                  message: 'Bv.: Ik neem elke dag 2 pilletjes bij het ontbijt',
                })}
                minHeight={DOSAGE_INPUT_MIN_HEIGHT}
              />
            }
            // Hidden until a medication is chosen, by search or by photo. Before
            // that it could only ever be disabled.
            submitButton={
              selectedMedication && (
                <Button
                  type='submit'
                  variant='Primary'
                  disabled={isMedicationSaveDisabled({
                    isSubmitting,
                    isValid,
                    hasSelectedMedication: !!selectedMedication,
                    // Key edit detection on the dialog mode (matches the submit
                    // path) rather than whether the item resolved — an update with
                    // a stale itemId must still skip the add-only content guards.
                    isEdit: dialog.mode === 'update',
                    dosageMode,
                    dosageRows: values.dosageRows,
                    asNeededBoolean: values.asNeededBoolean,
                    dosageInstructions: values.dosageInstructions,
                  })}
                >
                  {medicationToEdit
                    ? t({
                        id: 'medication.form.saveButton',
                        message: 'Opslaan',
                      })
                    : t({
                        id: 'medication.form.addButton',
                        message: 'Medicatie toevoegen',
                      })}
                </Button>
              )
            }
          >
            {/* Medication name search */}
            <SearchCombobox<MedicationSearchResult>
              id='medication-search'
              selectedName={selectedMedication?.name ?? null}
              results={results}
              getResultName={(r) => r.name}
              searchQuery={searchQuery}
              onSearchQueryChange={setSearchQuery}
              onSelect={(drug) => {
                mixpanel.track('medication_search_result_selected', {
                  query_length: searchQuery.length,
                  result_rank: results.indexOf(drug) + 1,
                  ...trackingProps,
                })
                setSelectedMedication({
                  name: drug.name,
                  atcCode: drug.atcCode,
                  atcName: drug.atcName,
                })
                setFieldValue('name', drug.name)
                setFieldValue('code', drug.atcCode ? { system: 'zorgi-article', value: drug.atcCode } : null)
                const unit = getUnitOptions().find((o) => o.value === drug.dosageUnit)?.value ?? 'pills'
                setFieldValue(
                  'dosageRows',
                  values.dosageRows.map((row) => ({ ...row, unit })),
                )
              }}
              onSelectFreeText={(name) => {
                mixpanel.track('medication_free_text_selected', {
                  query_length: searchQuery.length,
                  had_results: results.length > 0,
                  ...trackingProps,
                })
                setSelectedMedication({ name })
                setFieldValue('name', name)
                setFieldValue('code', null)
                setFieldValue(
                  'dosageRows',
                  values.dosageRows.map((row) => ({ ...row, unit: 'pills' })),
                )
              }}
            >
              <SearchCombobox.Label>
                {t({
                  id: 'medication.form.search.sectionLabel',
                  message: 'Zoek je medicatie',
                })}
              </SearchCombobox.Label>
              <SearchCombobox.Field>
                {isPhotoProcessed && (
                  <SearchCombobox.Adornment>
                    <StSearchProcessed>
                      <MedicationFileProcessed.Icon icon={['fal', 'image']} />
                      {/* No text next to the stand-in here, so screen readers get it from this. */}
                      <StVisuallyHidden>
                        {t({
                          id: 'medication.file.processed',
                          comment: 'Shown in place of a medication file the care team processed and removed',
                          message: 'Bestand verwerkt in je dossier',
                        })}
                      </StVisuallyHidden>
                    </StSearchProcessed>
                  </SearchCombobox.Adornment>
                )}
                {photoUrl && (
                  <SearchCombobox.Adornment>
                    <MedicationPhotoButton
                      aria-label={t({
                        id: 'medication.photo.open',
                        comment: 'Label of a medication photo thumbnail that opens the photo full screen',
                        message: 'Bekijk de foto van je medicatie',
                      })}
                      onClick={() => setPreviewUrl(photoUrl)}
                    >
                      <StSearchThumbnail
                        src={photoUrl}
                        alt={t({
                          id: 'medication.list.photoAlt',
                          message: 'Foto van je medicatie',
                        })}
                      />
                    </MedicationPhotoButton>
                  </SearchCombobox.Adornment>
                )}
                <SearchCombobox.Input
                  placeholder={t({
                    id: 'medication.form.search.placeholder',
                    message: 'Zoek geneesmiddel...',
                  })}
                />
                {selectedMedication ? (
                  <SearchCombobox.ClearButton
                    onClick={() => {
                      // The photo never reached an entry, so nothing downstream will
                      // revoke it.
                      if (attachment) {
                        URL.revokeObjectURL(attachment.url)
                      }

                      setSelectedMedication(null)
                      setAttachment(null)
                      setFieldValue('name', '')
                      setFieldValue('code', null)
                      setFieldValue('dosageRows', getDefaultDosageRows())
                    }}
                  >
                    <FontAwesomeIcon icon={['fal', 'times-circle']} />
                  </SearchCombobox.ClearButton>
                ) : (
                  <SearchCombobox.Icon icon={['fal', 'magnifying-glass']} />
                )}
              </SearchCombobox.Field>
              <SearchCombobox.Results
                freeTextLabel={(query) => (
                  <>
                    <Span type='BodyMediumBold'>{query}</Span>{' '}
                    <Trans id='medication.form.search.addFreeText'>toevoegen als nieuwe medicatie</Trans>
                  </>
                )}
              />
            </SearchCombobox>

            <ImagePreviewModal isVisible={!!previewUrl} onClose={() => setPreviewUrl(null)}>
              <ImagePreviewModal.Spinner />
              <ImagePreviewModal.Image
                src={previewUrl ?? undefined}
                alt={t({
                  id: 'medication.list.photoAlt',
                  message: 'Foto van je medicatie',
                })}
              />
              <ImagePreviewModal.CloseButton onClick={() => setPreviewUrl(null)} />
            </ImagePreviewModal>

            {showPhotoCard && (
              <MedicationPhotoCard>
                <MedicationPhotoCard.Icon icon={['far', 'camera']} />

                <div>
                  <MedicationPhotoCard.Title>
                    {t({
                      id: 'medication.form.photo.title',
                      message: 'Neem je liever een foto van je medicatiedoosje? Fotografeer één verpakking per foto.',
                    })}
                  </MedicationPhotoCard.Title>{' '}
                  <MedicationPhotoCard.Description>
                    {photoParsingEnabled
                      ? t({
                          id: 'medication.form.photo.description.ai',
                          message:
                            'Met behulp van artificiële intelligentie (AI) lezen we de naam van je medicatie op de foto. We vullen dan je medicatiegegevens al automatisch voor je in. Jij controleert daarna de voorgestelde gegevens en vult aan waar nodig. Zo helpt AI je om medicatie sneller toe te voegen.',
                        })
                      : t({
                          id: 'medication.form.photo.description',
                          message: 'Maak een foto van je medicatie-doosje en wij doen de rest.',
                        })}
                  </MedicationPhotoCard.Description>
                </div>

                {/* The information only covers the AI reading, so it is offered only while that is on. */}
                {photoParsingEnabled && (
                  <MedicationPhotoCard.Link onClick={() => setPhotoInfoOpen(true)}>
                    {t({
                      id: 'medication.form.photo.info.link',
                      message: 'Wat gebeurt er met mijn foto?',
                    })}
                  </MedicationPhotoCard.Link>
                )}

                <MedicationPhotoCard.Action
                  isLoading={isParsing || isUploading}
                  disabled={isParsing || isUploading}
                  onClick={() => handlePhoto(setFieldValue)}
                >
                  {t({
                    id: 'medication.form.photo.takePhoto',
                    message: 'Foto nemen',
                  })}
                </MedicationPhotoCard.Action>
              </MedicationPhotoCard>
            )}
          </StructuredObservation.ItemFormLayout>
        )
      }}
    </Formik>
  )
}

// ================================ Styled ================================

/** Sized to sit inside the search field without growing it. */
const StSearchThumbnail = styled(MedicationPhotoThumbnail)`
  width: ${({ theme }) => theme.UI.SpacingPx.Space9};
  height: ${({ theme }) => theme.UI.SpacingPx.Space9};
`

/** Sized like StSearchThumbnail, whose place it takes once the photo is processed. */
const StSearchProcessed = styled(MedicationFileProcessed)`
  width: ${({ theme }) => theme.UI.SpacingPx.Space9};
  height: ${({ theme }) => theme.UI.SpacingPx.Space9};
`

// TODO: shared with DateSelectionCarousel; move both to one VisuallyHidden primitive.
const StVisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`
